/**
 * Submitting an early-access request.
 *
 * Same-origin JSON in a `POST`, like the waitlist adapter: never a query
 * string, because a query string reaches server logs, browser history and
 * referrer headers, and a name and an address belong in none of them.
 *
 * **Success is the server's word, never this file's.** The dialog shows its
 * confirmation only for `ok`; every other branch is a truthful failure. No
 * client timestamp is sent — the server's clock is the one that counts —
 * and the optional link is sent exactly as typed, trimmed: the server
 * normalises it and nothing on either side retrieves it.
 */

import type { Language } from '../../i18n/translations.js';

export type EarlyAccessOutcome =
  | { readonly ok: true }
  /** The server refused the input. */
  | { readonly ok: false; readonly reason: 'rejected' }
  /** Too many attempts from this address. */
  | { readonly ok: false; readonly reason: 'rate_limited' }
  /** Offline, DNS failure, timeout, 5xx — worth retrying. */
  | { readonly ok: false; readonly reason: 'unavailable' }
  /** Aborted, normally because the dialog closed. Nothing is shown. */
  | { readonly ok: false; readonly reason: 'aborted' };

export interface EarlyAccessPayload {
  readonly name: string;
  readonly email: string;
  readonly brand: string;
  readonly link: string;
  readonly language: Language;
}

/** The one seam the page has to the network; the review page substitutes it. */
export type SubmitEarlyAccess = (
  payload: EarlyAccessPayload,
  signal: AbortSignal,
) => Promise<EarlyAccessOutcome>;

export const EARLY_ACCESS_ENDPOINT = '/public/early-access';

export const submitEarlyAccessRequest: SubmitEarlyAccess = async (payload, signal) => {
  let response: Response;
  try {
    response = await fetch(EARLY_ACCESS_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        name: payload.name.trim(),
        email: payload.email.trim(),
        brand: payload.brand.trim(),
        // Omitted rather than sent empty: an empty key says something was meant.
        ...(payload.link.trim().length > 0 ? { link: payload.link.trim() } : {}),
        language: payload.language,
      }),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, reason: 'aborted' };
    }
    return { ok: false, reason: 'unavailable' };
  }

  if (response.ok) return { ok: true };
  if (response.status === 429) return { ok: false, reason: 'rate_limited' };
  if (response.status >= 400 && response.status < 500) {
    return { ok: false, reason: 'rejected' };
  }
  return { ok: false, reason: 'unavailable' };
};
